/**
 * SearchResultRow — shared search result row for all tracker apps.
 *
 * Renders the image, title/metadata children, and two action buttons:
 *   📚 bookmark → floating dropdown list picker (portal + fixed positioning,
 *                 same useAddToList submenu as the detail modal three-dot menu)
 *   +            → calls onAction() to add to the collection
 *
 * @param {ReactNode}  props.image          - Rendered image element (app provides sizing/classes)
 * @param {string}     props.itemId         - ID stored in list_items.itemId (e.g. googleBooksId, traktID)
 * @param {string}     props.itemTitle      - Title stored in list_items.title
 * @param {string}     [props.itemImageURL] - Image URL stored in list_items.imageURL
 * @param {Object}     props.backendService
 * @param {string}     props.activeView
 * @param {Function}   props.onAction       - Adds item to the collection
 * @param {boolean}    props.isLoading
 * @param {boolean}    props.isDone
 * @param {ReactNode}  props.children       - Title / subtitle / badge JSX (in the flex-1 content area)
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useAddToList from '../hooks/useAddToList.jsx';

export default function SearchResultRow({
    image,
    itemId,
    itemTitle,
    itemImageURL = '',
    backendService,
    activeView,
    onAction,
    isLoading,
    isDone,
    children,
}) {
    const [listPickerOpen, setListPickerOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const bookmarkBtnRef = useRef(null);
    const dropdownRef = useRef(null);

    const getItemData = useCallback(() => ({
        itemId: itemId || '',
        title: itemTitle,
        imageURL: itemImageURL || '',
    }), [itemId, itemTitle, itemImageURL]);

    const { addToListAction } = useAddToList({ backendService, activeView, getItemData });

    const handleBookmarkClick = useCallback((e) => {
        e.stopPropagation();
        if (!listPickerOpen) {
            addToListAction.onOpen?.();
            // Position dropdown below the bookmark button, aligned to its right edge
            const rect = bookmarkBtnRef.current?.getBoundingClientRect();
            if (rect) {
                const DROPDOWN_WIDTH = 208; // w-52
                // Prefer right-aligned to button; clamp to viewport
                let left = rect.right - DROPDOWN_WIDTH;
                if (left < 8) left = 8;
                if (left + DROPDOWN_WIDTH > window.innerWidth - 8) left = window.innerWidth - 8 - DROPDOWN_WIDTH;
                // Position below button; flip above if not enough space
                const spaceBelow = window.innerHeight - rect.bottom;
                const top = spaceBelow >= 200
                    ? rect.bottom + 4
                    : rect.top - 4; // will use transform translateY(-100%) when flipped
                setDropdownStyle({
                    position: 'fixed',
                    top,
                    left,
                    width: DROPDOWN_WIDTH,
                    zIndex: 9999,
                    ...(spaceBelow < 200 && { transform: 'translateY(-100%)' }),
                });
            }
        }
        setListPickerOpen((open) => !open);
    }, [listPickerOpen, addToListAction]);

    // Close on outside click, outside scroll, or Escape
    useEffect(() => {
        if (!listPickerOpen) return;
        const close = () => setListPickerOpen(false);
        const handleClick = (e) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                bookmarkBtnRef.current && !bookmarkBtnRef.current.contains(e.target)
            ) close();
        };
        const handleScroll = (e) => {
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            close();
        };
        const handleKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [listPickerOpen]);

    const dropdown = listPickerOpen && createPortal(
        <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="rounded-lg bg-surface border border-border-subtle shadow-xl py-1 overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
        >
            {addToListAction.renderSubmenu({ close: () => setListPickerOpen(false) })}
        </div>,
        document.body,
    );

    return (
        <>
            {image}
            <div className="min-w-0 flex-1">
                {children}
            </div>

            <div className="flex-shrink-0 flex items-center gap-1">
                {isDone ? (
                    <span className="text-xs text-green-500 font-medium">Added ✓</span>
                ) : isLoading ? (
                    <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                ) : (
                    <>
                        {/* Add to list */}
                        <button
                            ref={bookmarkBtnRef}
                            onClick={handleBookmarkClick}
                            title="Add to list"
                            className={['p-1 transition-colors', listPickerOpen ? 'text-accent-primary' : 'text-text-tertiary hover:text-accent-primary'].join(' ')}
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
                            </svg>
                        </button>
                        {/* Add to collection */}
                        <button
                            onClick={(e) => { e.stopPropagation(); onAction(); }}
                            title="Add to collection"
                            className="p-1 text-text-tertiary hover:text-accent-primary transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                        </button>
                    </>
                )}
            </div>

            {dropdown}
        </>
    );
}
